import React, { ReactElement, useCallback, useContext, useEffect, useState } from 'react';
import ContainerLayout from 'layouts/ContainerLayout/ContainerLayout';
import ConditionalChildren from 'layouts/ConditionalChildren/ConditionalChildren';
import { Interpolate, TranslationContext } from '@ds/react-utils';
import { useHistory } from 'react-router-dom';
import { IPhoneNumber } from 'services/PhoneNumber';
import { first } from 'lodash';
import { Header, ProgressCircle, RadioButton, RadioButtonGroup } from '@ds/ui';
import replaceStarsWithBulletPoints from 'utils/replaceStarsWithBulletPoints/replaceStarsWithBulletPoints';
import styled from '@emotion/styled';
import { FontDetailS, FontBodyL, ModalOnLink } from 'components/shared';
import { useButtonsLoading, useNavigateToErrorPage } from 'hooks';
import { routes } from 'static/routes';
import Center = Header.Center;
import VerifyApplicationContext from 'contexts/VerifyApplicationContext';
import { getPhoneNumbers, sendCall, sendSMS } from 'queries/PhoneQueries';
import { AxiosError } from 'axios';

const PhoneNumber = styled.span`
    font-weight: bold;
`;

const ChooseMethodPage = (): ReactElement => {
    const context = useContext(VerifyApplicationContext);
    const history = useHistory();
    const translate = React.useContext(TranslationContext);
    const navigateToErrorPage = useNavigateToErrorPage();

    const {
        isPrimaryButtonLoading,
        setIsPrimaryButtonLoading,
        isSecondaryButtonLoading,
        setIsSecondaryButtonLoading,
        areButtonsLoading
    } = useButtonsLoading();

    const [phoneNumberList, setPhoneNumberList] = useState<IPhoneNumber[] | undefined>(undefined);
    const [selectedPhoneNumber, setSelectedPhoneNumber] = useState<IPhoneNumber | undefined>(undefined);

    useEffect(() => {
        if (context.isDocumentLocked) {
            history.replace(routes.DocumentLocked);
        }
    }, [context, history]);

    const onSmsClick = useCallback(async () => {
        if (areButtonsLoading) {
            return;
        }

        if (!selectedPhoneNumber) {
            return;
        }

        setIsPrimaryButtonLoading(true);

        sendSMS(
            {
                phoneNumber: selectedPhoneNumber.phoneNumber,
                uniqueId: selectedPhoneNumber.uniqueId,
                language: context.language
            },
            () => {
                setIsPrimaryButtonLoading(false);
                history.push(routes.ConfirmCode, { selectedPhoneNumber: selectedPhoneNumber, method: 'SMS' });
            },
            (error: AxiosError) => {
                setIsPrimaryButtonLoading(false);
                if (error?.response?.status === 401) {
                    history.push(routes.SessionExpired);
                }
            }
        );
    }, [areButtonsLoading, setIsPrimaryButtonLoading, history, selectedPhoneNumber, context]);

    const onCallClick = useCallback(() => {
        if (areButtonsLoading) {
            return;
        }

        if (!selectedPhoneNumber) {
            return;
        }

        setIsSecondaryButtonLoading(true);

        sendCall(
            {
                phoneNumber: selectedPhoneNumber.phoneNumber,
                uniqueId: selectedPhoneNumber.uniqueId,
                language: context.language
            },
            () => {
                setIsSecondaryButtonLoading(false);
                history.push(routes.ConfirmCode, { selectedPhoneNumber: selectedPhoneNumber, method: 'PhoneCall' });
            },
            (error: AxiosError) => {
                setIsSecondaryButtonLoading(false);
                if (error?.response?.status === 401) {
                    history.push(routes.SessionExpired);
                }
            }
        );
    }, [areButtonsLoading, setIsSecondaryButtonLoading, history, selectedPhoneNumber, context]);

    useEffect(() => {
        getPhoneNumbers(
            (response: IPhoneNumber[]) => {
                if (!response || !response.length) {
                    navigateToErrorPage('No phone number');
                    return;
                }
                setPhoneNumberList(response);
                setSelectedPhoneNumber(first(response));
            },
            (error: AxiosError) => {
                if (error?.response?.status === 401) {
                    history.push(routes.SessionExpired);
                } else {
                    navigateToErrorPage(error?.message || 'Exception');
                }
            }
        );
    }, [history]);

    if (phoneNumberList === undefined || selectedPhoneNumber === undefined) {
        return (
            <ContainerLayout>
                <ContainerLayout.InnerContent>
                    <Center>
                        <ProgressCircle hideText text="Loading"></ProgressCircle>
                    </Center>
                </ContainerLayout.InnerContent>
            </ContainerLayout>
        );
    }

    return (
        <ContainerLayout>
            <ContainerLayout.Header>
                <ContainerLayout.Header.Title
                    title={translate("To keep you safe, confirm it's really you")}
                    titleDataQa="verify-choose-method-title"
                />
            </ContainerLayout.Header>

            <ContainerLayout.InnerContent>
                <FontBodyL data-qa="verify-choose-method-intro">
                    <ConditionalChildren condition={context.displayChannel === 'PhoneCallOnly'} fallback={<>{translate("We'll send a code to")}</>}>
                        {translate("We'll give you a code on")}
                    </ConditionalChildren>
                </FontBodyL>
                <ConditionalChildren
                    condition={phoneNumberList.length > 1}
                    fallback={
                        <FontBodyL data-qa="verify-choose-method-phone-number">
                            <PhoneNumber>{replaceStarsWithBulletPoints(first(phoneNumberList)?.phoneNumber ?? '')}</PhoneNumber>
                        </FontBodyL>
                    }
                >
                    <RadioButtonGroup key="Ink" groupIdentifier="phone-number-radios" required legend="">
                        {phoneNumberList.map((phoneNumber: IPhoneNumber, index) => {
                            const maskedPhoneNumber = replaceStarsWithBulletPoints(phoneNumber.phoneNumber ?? '');
                            return (
                                <RadioButton
                                    checked={phoneNumber.uniqueId === selectedPhoneNumber.uniqueId}
                                    label={<FontBodyL data-qa="verify-choose-method-phone-numbers">{maskedPhoneNumber}</FontBodyL>}
                                    value={phoneNumber.uniqueId}
                                    key={phoneNumber.uniqueId}
                                    onChange={() => setSelectedPhoneNumber(phoneNumber)}
                                />
                            );
                        })}
                    </RadioButtonGroup>
                </ConditionalChildren>
                <ModalOnLink
                    linkDataQa="verify-choose-method-modal-link-not-my-number"
                    headerDataQa="verify-choose-method-modal-title-not-my-number"
                    contentDataQa="verify-choose-method-modal-content-not-my-number"
                    linkText={translate("That's Not My Number")}
                    modalHeaderTitle={translate("That's not my number")}
                >
                    <p>{translate("To continue, you'll need to ask the sender to update your phone number.")}</p>
                </ModalOnLink>
            </ContainerLayout.InnerContent>

            <ContainerLayout.Footer>
                <ContainerLayout.Footer.Disclaimer>
                    <FontDetailS>
                        <ConditionalChildren condition={context.displayChannel === 'SMSandPhoneCall'}>
                            <Interpolate
                                text={translate(
                                    "By selecting <BoldTextSendNow>[[Placeholder for 'Send Now']]</BoldTextSendNow> to receive a text message, you agree to receive a one-time passcode. If you select <BoldTextGetACall>[[Placeholder for 'Get a Call Instead']]</BoldTextGetACall> to receive a phone call, you agree to the use of artificial or prerecorded voice and automated systems. Message and data rates may apply."
                                )}
                                components={{
                                    BoldTextSendNow: () => <strong>{translate('Send Now')}</strong>,
                                    BoldTextGetACall: () => <strong>{translate('Get a Call Instead')}</strong>
                                }}
                            />
                        </ConditionalChildren>
                        <ConditionalChildren condition={context.displayChannel === 'SMSOnly'}>
                            <Interpolate
                                text={translate(
                                    "By selecting <BoldText>[[Placeholder for 'Send Now']]</BoldText> to receive a text message, you agree to receive a one-time passcode. Automated systems may be used. Message and data rates may apply."
                                )}
                                components={{
                                    BoldText: () => <strong>{translate('Send Now')}</strong>
                                }}
                            />
                        </ConditionalChildren>
                        <ConditionalChildren condition={context.displayChannel === 'PhoneCallOnly'}>
                            <Interpolate
                                text={translate(
                                    "By selecting <BoldText>[[Placeholder for 'Get a Call']]</BoldText> to receive a phone call, you agree to the use of artificial or prerecorded voice and automated systems. Message and data rates may apply."
                                )}
                                components={{
                                    BoldText: () => <strong>{translate('Get a Call')}</strong>
                                }}
                            />
                        </ConditionalChildren>
                    </FontDetailS>
                </ContainerLayout.Footer.Disclaimer>
                <ContainerLayout.Footer.Buttons>
                    <ConditionalChildren condition={context.displayChannel === 'SMSandPhoneCall'}>
                        <ContainerLayout.Footer.Buttons.Secondary
                            data-qa="verify-choose-method-secondary-button"
                            text={translate('Get a Call Instead')}
                            onClick={onCallClick}
                            loading={isSecondaryButtonLoading}
                        />
                        <ContainerLayout.Footer.Buttons.Primary
                            data-qa="verify-choose-method-primary-button"
                            text={translate('Send Now')}
                            onClick={onSmsClick}
                            loading={isPrimaryButtonLoading}
                        />
                    </ConditionalChildren>
                    <ConditionalChildren condition={context.displayChannel === 'PhoneCallOnly'}>
                        <ContainerLayout.Footer.Buttons.Primary
                            data-qa="verify-choose-method-primary-button"
                            text={translate('Get a Call')}
                            onClick={onCallClick}
                            loading={isPrimaryButtonLoading}
                        />
                    </ConditionalChildren>
                    <ConditionalChildren condition={context.displayChannel === 'SMSOnly'}>
                        <ContainerLayout.Footer.Buttons.Primary
                            data-qa="verify-choose-method-primary-button"
                            text={translate('Send Now')}
                            onClick={onSmsClick}
                            loading={isPrimaryButtonLoading}
                        />
                    </ConditionalChildren>
                </ContainerLayout.Footer.Buttons>
            </ContainerLayout.Footer>
        </ContainerLayout>
    );
};

export default ChooseMethodPage;
